import { moneefloApi } from "./client";
import { coreError } from "./core-error";

/**
 * The forex order screen (`/forex/orderdetails/:id`), answered by the new
 * backend.
 *
 * Xano served this screen from `order_details_v3`, one response that carried
 * the order, its currency lines, deliveries, cards, documents and a milestone
 * list. Core's staff order detail now carries the order, its lines
 * (`items[]`), the forex mode, delivery, cards, trips, coupon and discount;
 * `/documents` the paperwork; `/forex-checks` what would stop it being
 * confirmed or released. Per-line decisions, payment decisions, a
 * cancellation fee and the desk's actions (see legacy-forex-desk-api.ts) have
 * their own routes.
 *
 * Money arrives as integer paise (strings) and rates as micros, and is shown
 * here in rupees. Nothing is calculated: every figure is one the server
 * already priced and stored. Ops say sale and encashment; "buy" and "sell"
 * only survive as the values the screen's own tabs switch on.
 */

const rupees = (minor: string | null | undefined): number =>
  Number(minor ?? 0) / 100;

type OrgContact = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
};

type CoreOrder = {
  id: string;
  orderNo: string;
  status: string;
  paymentStatus: string;
  product: string | null;
  currency: string | null;
  fxAmountMinor: string | null;
  fxRate: string | null;
  totalPayableMinor: string | null;
  placedAt: string | null;
  createdBy: { name: string } | null;
  customer: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
  };
  identities: { docType: string; last4: string | null; verified: boolean }[];
  charges: {
    type: string;
    label: string | null;
    amountMinor: string;
    taxMinor: string;
  }[];
  payments: {
    id: string;
    /** The payment's uuid, which deciding it needs. `id` is a row number. */
    publicId?: string;
    /** Set when the desk has to check the payment before it counts. */
    deskVerification?: "awaiting" | "accepted" | "rejected" | null;
    /** True when the person looking recorded it, and so cannot accept it. */
    recordedByMe?: boolean;
    reference: string | null;
    amountMinor: string;
    method: string;
    status: string;
    receivedAt: string | null;
    /** The document filed as this payment's proof, and any supporting documents (exact links from core). */
    proofDocumentId?: string | null;
    supportingDocumentIds?: string[];
  }[];
  overpaidMinor?: string;
  refundDueMinor?: string;
  /** What core has worked out; the screen only displays them (paise, as strings). */
  paidMinor?: string;
  refundedMinor?: string;
  balanceMinor?: string;
  refundableMinor?: string;
  largestSingleRefundMinor?: string;
  linesAmountBeforeTaxMinor?: string;
  originOrganization: OrgContact | null;
  settlementRate?: string | null;
  forexMode?: "sale" | "encashment" | null;
  items?: {
    id: string;
    product: string;
    productId: number | null;
    currencyWant: string | null;
    currencyHave: string | null;
    fxAmountMinor: string;
    inrAmountMinor: string;
    amountBeforeTaxMinor?: string;
    rateMicros: string;
    status: "pending" | "accepted" | "rejected";
    statusReason: string | null;
    card?: { id: string; last4: string; issuer: string | null } | null;
  }[];
  deliveries?: CoreDelivery[];
  delivery?: {
    address: {
      id: string;
      line1: string;
      line2: string | null;
      landmark: string | null;
      city: string;
      state: string | null;
      postalCode: string;
      country: string;
    };
    status: "not_required" | "pending" | "delivered" | "cancelled";
  } | null;
  trips?: {
    id: string;
    destinationCountry: string | null;
    departureAt: string | null;
    returnAt: string | null;
    purpose: string | null;
    travellers: { id: string; fullName: string; screeningStatus: string }[];
  }[];
  coupon?: { code: string; kind: string } | null;
  discountMinor?: string;
  forex?: {
    residency: string;
    lrsSigned: boolean;
    declaredOnArrival?: boolean;
    notes?: string | null;
    tcsOverride?: { amountMinor: string; reason: string } | null;
    serviceChargeOverrideMinor?: string | null;
  } | null;
};

export type CoreDelivery = {
  id: string;
  mode: "courier" | "self" | "pickup";
  channel: string | null;
  status: "pending" | "ready_to_deliver" | "delivered" | "failed" | "cancelled";
  scheduledOn: string | null;
  deliveredAt: string | null;
  notes: string | null;
  address: {
    line1: string;
    line2: string | null;
    landmark: string | null;
    city: string;
    state: string | null;
    postalCode: string;
    country: string;
  } | null;
  assignedTo: { id: string; name: string } | null;
  items: { lineId: string; product: string; currency: string; fxAmountMinor: string }[];
};

export type ForexFinding = {
  code: string;
  message: string;
  stage: "confirm" | "release";
  severity: "block" | "warn";
};

type ForexChecks = { findings: ForexFinding[] };

type CoreDocument = {
  id: string;
  docType: string;
  fileName: string | null;
  label: string | null;
  status: string;
  uploadedAt: string;
  contentType: string | null;
  url?: string | null;
  /** Present once core says which requirement a document answers. */
  requirementId?: string | null;
  /** Why a rejected document was rejected, if core says. */
  reason?: string | null;
  rejectionReason?: string | null;
};

type CoreDocuments = {
  requirements: { id: string; label: string; mandatory?: boolean; satisfied: boolean }[];
  documents: CoreDocument[];
  /** The customer's own papers (PAN, passport scans), held once against them. */
  customerDocuments?: CoreDocument[];
};

const ddmmyyyy = (iso: string) => {
  const d = new Date(iso);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const charge = (order: CoreOrder, ...types: string[]): number => {
  // Added up in paise, then shown in rupees.
  const minor = order.charges
    .filter((c) => types.includes(c.type))
    .reduce((sum, c) => sum + BigInt(c.amountMinor || "0") + BigInt(c.taxMinor || "0"), 0n);
  return Number(minor) / 100;
};

const masked = (order: CoreOrder, docType: string): string => {
  const found = order.identities.find((i) => i.docType === docType);
  // Only the last four ever leave the database. Never rebuilt on the client.
  return found?.last4 ? `••••${found.last4}` : "";
};

type Stage =
  | "Order Created"
  | "Order Accepted"
  | "Documents"
  | "Payments"
  | "Delivery"
  | "Order Closed"
  | "Order Cancelled"
  | "Refund Completed";

const FLOW: Stage[] = [
  "Order Created",
  "Order Accepted",
  "Documents",
  "Payments",
  "Delivery",
  "Order Closed",
];

/**
 * The screen's milestone tracker, worked out from the one status the new
 * backend keeps. Xano stored a milestone list per order; here there is a
 * single lifecycle, so the stage the order is in is read off it, together
 * with whether its documents are complete and whether it is paid.
 *
 * Exactly one milestone is Active unless the order has finished, because the
 * screen decides which buttons to show from the highest Active stage.
 */
function milestonesFor(order: CoreOrder, docs: CoreDocuments, docsUnknown = false) {
  const make = (
    stage: Stage,
    status: "Fulfilled" | "Active" | "Inactive",
    priority: number,
  ) => ({
    milestone_id: `${order.id}:${stage}`,
    status,
    product_id: [] as string[],
    sub_stages: [] as unknown[],
    stage,
    priority,
  });

  if (order.status === "cancelled") {
    return [
      make("Order Created", "Fulfilled", 1),
      make("Order Cancelled", "Fulfilled", 99),
    ];
  }

  let reached = 0; // index in FLOW of the stage that is Active
  if (order.status === "fulfilled" || order.status === "refunded") {
    reached = FLOW.length; // everything done
  } else if (order.status === "draft" || order.status === "quoted") {
    reached = 0;
  } else {
    // An optional requirement never holds the order back; only the mandatory ones do.
    // If the documents could not be read they are not assumed complete.
    const docsDone = !docsUnknown && docs.requirements.filter((r) => r.mandatory !== false).every((r) => r.satisfied);
    // Core marks a fully paid order "received" (older rows may say "paid").
    // Core says what is still owed; nothing is added up here.
    // No balance from core means paid is unknown, and unknown is not paid.
    const paid = order.balanceMinor !== undefined && /^0+$/.test(order.balanceMinor);
    if (!docsDone) reached = 2;
    else if (!paid) reached = 3;
    else reached = 4;
  }

  const rows = FLOW.map((stage, index) =>
    make(
      stage,
      index < reached ? "Fulfilled" : index === reached ? "Active" : "Inactive",
      index + 1,
    ),
  );
  if (order.status === "refunded") rows.push(make("Refund Completed", "Fulfilled", 98));
  return rows;
}

/** `/forex/orderdetails/<ref>` or `/cancel/forex/<ref>`: the order on screen. */
export function orderRefFromRoute(): string {
  const match = /\/(?:forex\/orderdetails|cancel\/forex|refund\/forex)\/([^/?#]+)/.exec(
    window.location.pathname,
  );
  if (!match) {
    throw new Error("Open an order first: this action works on the order on screen.");
  }
  return decodeURIComponent(match[1]);
}

const PAYMENT_STATUS: Record<string, string> = {
  received: "accept",
  failed: "reject",
  refunded: "refund",
  pending: "pending",
};

/** Core's line status in the words the screen's accept and reject controls use. */
const LINE_STATUS: Record<string, string> = {
  accepted: "Accept",
  rejected: "Reject",
  pending: "",
};

const DELIVERY_STATUS: Record<string, string> = {
  pending: "Pending",
  ready_to_deliver: "Ready to deliver",
  delivered: "Delivered",
  failed: "Failed",
  cancelled: "Cancelled",
};

/**
 * One forex order, in the shape the screen was built against.
 *
 * Errors are left to the caller: a 404 is how the screen knows the order does
 * not exist, which is different from the API being unreachable.
 */
export async function getForexOrderDetails(orderRef: string, options: { readChecks?: boolean } = {}) {
  const readChecks = options.readChecks !== false;
  const { data: order } = await moneefloApi.get<CoreOrder>(
    `/orders/${encodeURIComponent(orderRef)}`,
  );
  const id = encodeURIComponent(order.id);
  // Only an order placed from the forex desk carries forex details. Core keeps
  // the desk's checks and edits to those; one from the website, a partner or
  // the old system is read, paid and cancelled like any other order.
  const deskPlaced = order.forex != null;

  // Documents and what would stop the order moving on are parts of a busy
  // screen: a failure in one (a finance user holds no forex.desk, so the
  // checks refuse them) must not empty the rest.
  // A part that cannot be read is reported as not loaded, never shown as complete or as having nothing to say.
  const loadFailed: string[] = [];
  const [docs, checks] = await Promise.all([
    moneefloApi
      .get<CoreDocuments>(`/orders/${id}/documents`)
      .then((r) => r.data)
      .catch((): CoreDocuments => {
        loadFailed.push("documents");
        return { requirements: [], documents: [] };
      }),
    readChecks && deskPlaced
      ? moneefloApi
          .get<ForexChecks>(`/orders/${id}/forex-checks`)
          .then((r) => r.data)
          .catch((): ForexChecks => {
            loadFailed.push("what is needed before the order can move on");
            return { findings: [] };
          })
      : Promise.resolve<ForexChecks>({ findings: [] }),
  ]);

  const total = rupees(order.totalPayableMinor);
  // Every money figure the screen shows as paid, owed or refundable is core's own.
  const money = {
    paidMinor: order.paidMinor ?? "0",
    refundedMinor: order.refundedMinor ?? "0",
    // Left null when core does not say, so the screen never reads a missing balance as "nothing owed".
    balanceMinor: order.balanceMinor ?? null,
    refundableMinor: order.refundableMinor ?? "0",
    largestSingleRefundMinor: order.largestSingleRefundMinor ?? "0",
    overpaidMinor: order.overpaidMinor ?? "0",
    linesAmountBeforeTaxMinor: order.linesAmountBeforeTaxMinor ?? "0",
  };
  const paid = rupees(money.paidMinor);
  const gst = charge(order, "gst");
  // Half each to the centre and the state, worked in paise so the halves add
  // up to the GST exactly: an odd paisa goes to CGST.
  const gstMinor = Math.round(gst * 100);
  const sgstMinor = Math.floor(gstMinor / 2);
  const cgstMinor = gstMinor - sgstMinor;
  const serviceCharge = charge(order, "service_charge", "service_fee");
  const tcs = charge(order, "tcs");
  const discount = rupees(order.discountMinor);
  const mode = order.forexMode ?? "sale";
  // The screen's own words for the two directions: a sale is "buy" and an
  // encashment is "sell" inside its tabs, and the header says sale or encashment.
  const direction = mode === "encashment" ? "sell" : "buy";

  const fileOf = (d: CoreDocument) =>
    d.url
      ? {
          url: d.url,
          name: d.fileName ?? d.docType,
          type: d.contentType?.includes("pdf") ? "pdf" : "image",
          mime: d.contentType ?? "",
        }
      : null;
  // The customer's PAN and passport scans, for the KYC review.
  const scanOf = (match: RegExp) => {
    const found = (docs.customerDocuments ?? []).find((d) => match.test(d.docType) && d.url);
    return found ? fileOf(found) : undefined;
  };

  const lines = (order.items ?? []).map((item) => {
    const fx = rupees(item.fxAmountMinor);
    const rate = Number(item.rateMicros) / 1_000_000;
    return {
      id: item.id,
      created_for: order.customer.id as unknown as number,
      product_id: item.productId ?? 0,
      fx_amount: fx,
      inr_amount: rupees(item.inrAmountMinor),
      currency_have: item.currencyHave ?? "INR",
      currency_want: item.currencyWant ?? "INR",
      city_country: "",
      rate,
      rate_updated_at: null,
      status: LINE_STATUS[item.status] ?? "",
      start_date: null,
      end_date: null,
      country_currency_want: item.currencyWant ?? "INR",
      country_currency_have: item.currencyHave ?? "INR",
      // The screen shows the foreign amount as the line's quantity.
      quantity: fx,
      reason_for_reject: item.statusReason ?? "",
      fx_amount_minor: item.fxAmountMinor,
      rate_and_margins: [
        { l1_commission: 0, l2_commission: 0, l3_commission: 0, l2_id: 0, ibr_rate: rate, rate_updated_at: null },
      ],
      customer_detail: {
        full_name: order.customer.name,
        documents_id: null,
        kyc_status: order.identities.some((i) => i.verified) ? "active" : "pending",
        pan_soft_copy: scanOf(/\bpan\b/i),
        passport_front: scanOf(/passport(?!.*back)/i),
        passport_back: scanOf(/passport.*back/i),
      },
      name: item.product,
      subCategory: "",
    };
  });
  // A rejected line stays on the order and is not charged; core says what the coupon is checked against.
  const amountBeforeTax = rupees(money.linesAmountBeforeTaxMinor);

  const amountAndMargin = {
    TT_charges: charge(order, "tt_charge"),
    TCS: tcs,
    Foreign_Charges: charge(order, "bank_charge"),
    Customer_Rate: Number(order.fxRate ?? 0),
    Partner_Rate: order.settlementRate ? Number(order.settlementRate) / 1_000_000 : 0,
    Moneeflo_Margin: 0,
    Settlement_Rate: order.settlementRate ? Number(order.settlementRate) / 1_000_000 : 0,
    Partner_Margin: 0,
    GST: {
      GST: gst,
      CGST: cgstMinor / 100,
      SGST: sgstMinor / 100,
      IGST: 0,
      round_off: 0,
    },
    service_charge: { amount: serviceCharge },
  };

  // A superseded document is history, not something to review.
  const orderDocuments = docs.documents
    .filter((d) => d.status !== "superseded")
    .map((d) => ({
      id: d.id,
      doc_list_id: d.id,
      doc_name: d.label || d.fileName || d.docType,
      status: d.status,
      type_of_doc: d.docType,
      // Every document on a forex order is the order's customer's.
      created_by_cx: order.customer.id,
      upload_date: ddmmyyyy(d.uploadedAt),
      requirement_id: d.requirementId ?? null,
      rejection_reason: d.rejectionReason ?? d.reason ?? null,
      document: fileOf(d),
    }));
  // One row per line on each delivery, which is what the screen's tabs read.
  // The deliveries themselves, with their ids, go through as `deliveries`.
  const deliveryRows = (order.deliveries ?? []).flatMap((d) =>
    d.items.map((item) => ({
      deliver_to: 0,
      delivery_mode: d.mode,
      delivery_id: d.id,
      tracking_id: "",
      generic_order_product_id: item.lineId,
      delivery_channel: d.channel ?? "",
      Status: DELIVERY_STATUS[d.status] ?? d.status,
      delivered_at: d.deliveredAt ?? "",
      adress: d.address ? [d.address.line1, d.address.city].filter(Boolean).join(", ") : "",
      address_detail: {
        street_number: d.address ? [d.address.line1, d.address.line2].filter(Boolean).join(", ") : "",
        landmark: d.address?.landmark ?? "",
        City: d.address?.city ?? "",
        State: d.address?.state ?? "",
        pincode: d.address?.postalCode ?? "",
        country: d.address?.country ?? "",
        id: "",
      },
      full_name: order.customer.name,
      product_name: item.product,
      uuid: `${d.id}:${item.lineId}`,
      phone_number: order.customer.phone ?? "",
      assign_to: d.assignedTo?.id ?? "",
    })),
  );

  const docById = (id: string | null | undefined) => (id ? docs.documents.find((d) => d.id === id && d.url) ?? null : null);

  const trip = order.trips?.[0];
  const tripDetails = trip
    ? {
        id: 0,
        country: trip.destinationCountry ?? "",
        start_date: trip.departureAt ?? "",
        end_date: trip.returnAt ?? "",
        trip_name: trip.purpose ?? "",
        trip_status: "",
        visa: null,
        departure: {},
        return: {},
        extra_details: {
          traveller_name: trip.travellers.map((t) => t.fullName),
          visa_info: "",
          travellers: trip.travellers.map((t) => ({
            traveller_name: t.fullName,
            kyc: t.screeningStatus === "clear",
          })),
        },
      }
    : undefined;

  // A card is linked to a line: one entry per line that has one. Only the last four digits ever leave the database.
  const cardDetails = (order.items ?? [])
    .filter((i) => i.card)
    .map((i, index) => ({
      id: index + 1,
      created_at: 0,
      card_holder: 0,
      card_company: i.card?.issuer ?? "",
      card_number: `••••${i.card?.last4}`,
      card_status: "",
      forex_card_id: 0,
      action_type: "",
      _forex_card: [],
    }));

  const next = order.status === "draft" || order.status === "quoted" ? "confirm" : "release";
  const open = ["draft", "quoted", "confirmed", "processing", "on_hold"].includes(order.status);
  const findings = open
    ? checks.findings.filter((f) => next === "release" || f.stage === "confirm")
    : [];

  return {
    id: order.id,
    metadata: {
      created_at: order.placedAt ? new Date(order.placedAt).getTime() : 0,
      created_by: order.createdBy?.name ?? "",
      // The bare reference staff say out loud, as the orders list shows it.
      order_id: order.orderNo.replace(/^(ORD-)?MF-/, ""),
      order_status: order.status,
      product_group: order.product ?? "",
      source: order.originOrganization?.name ?? "N/A",
      created_for: order.customer.name,
      customer_id: order.customer.id,
      type_of_order: mode === "encashment" ? "encashment" : direction,
    },
    created_by_staff: 0,
    created_by_customer: order.customer.id,
    milestones: milestonesFor(order, docs, loadFailed.includes("documents")),
    customers: { product_customer: lines },
    delivery: deliveryRows,
    card_details: cardDetails,
    trip_details: tripDetails,
    order_documents: orderDocuments,
    // The newest invoice filed on the order, which is what the header offers to download.
    invoice_document: (() => {
      const found = docs.documents.find((d) => d.docType === "invoice" && d.url);
      return found ? { id: found.id, doc_name: found.fileName ?? "invoice", document: fileOf(found) } : null;
    })(),
    payment_detail: order.payments.map((p) => ({
      id: p.publicId ?? p.id,
      acknowledgement_no: p.reference ?? "",
      desk_verification: p.deskVerification ?? null,
      recorded_by_me: p.recordedByMe === true,
      amount: rupees(p.amountMinor),
      status_by_AD2: PAYMENT_STATUS[p.status] ?? p.status,
      // Core links each payment to its own proof and supporting documents. A payment with no linked
      // proof has none: nothing is guessed from other payments' files.
      receipt: (() => {
        const found = docById(p.proofDocumentId);
        return found ? fileOf(found) : { url: "" };
      })(),
      supporting_doc: (() => {
        const found = docById(p.supportingDocumentIds?.[0]);
        return found ? fileOf(found) : null;
      })(),
      created_at: p.receivedAt ? new Date(p.receivedAt).getTime() : 0,
      mode: ["payu", "cashfree"].includes(p.method) ? "online" : "offline",
      fname: "",
    })),
    overview: {
      products: lines,
      amount_and_charges: {
        total_payable: total,
        total_paid: paid,
        money,
        // Paid beyond the bill, and what is owed back to the customer.
        overpaid: rupees(order.overpaidMinor),
        refund_due: rupees(order.refundDueMinor),
        margins_and_charges: amountAndMargin,
      },
      notes: order.forex?.notes ?? "",
      note_last_edited: "",
      delivery_info: deliveryRows,
      lrs_signed: order.forex?.lrsSigned ?? false,
    },
    amount_and_charges: {
      product_list: {},
      amount_and_margin: amountAndMargin,
      total_payable: total,
      amount_before_tax: amountBeforeTax,
      service_charge: serviceCharge,
      discount_info: discountInfo(order, discount),
    },
    discount_info: discountInfo(order, discount),
    other_detail: {
      Delivery_partners: [] as string[],
      comapny_detail: {},
      lead_ids: [] as number[],
      reffered_partner: {},
      // Read by the Other Details tab for the remitter card.
      _order_customers: {
        full_name: order.customer.name,
        email: order.customer.email ?? "",
        phone_number: order.customer.phone ?? "",
        pan_no: masked(order, "pan"),
      },
    },
    // Deliveries as core keeps them, and the order's own desk fields.
    deliveries: order.deliveries ?? [],
    money,
    deliveryAddress: order.delivery?.address ?? null,
    declaredOnArrival: order.forex?.declaredOnArrival ?? false,
    tcsOverride: order.forex?.tcsOverride
      ? { amount: rupees(order.forex.tcsOverride.amountMinor), reason: order.forex.tcsOverride.reason }
      : null,
    serviceChargeOverride:
      order.forex?.serviceChargeOverrideMinor != null
        ? rupees(order.forex.serviceChargeOverrideMinor)
        : null,
    // Where each card line stands, for the card control on the line.
    line_cards: Object.fromEntries(
      (order.items ?? []).filter((i) => i.card).map((i) => [i.id, i.card]),
    ),
    // What would stop this order moving on, in core's own words.
    forexFindings: findings,
    loadFailed,
    forexNextStep: next,
    deskPlaced,
  };
}

function discountInfo(order: CoreOrder, discount: number) {
  return {
    amount_discounted: discount,
    discount_coupon_id: order.coupon
      ? [{ coupon_name: order.coupon.code, id: order.coupon.code, quantity: 0, type: order.coupon.kind }]
      : [],
  };
}

/* ------------------------------------------------------------ actions -- */

/** How the counter payment arrived, as core's payment methods. */
export type CounterPaymentMethod = "cash" | "upi" | "neft" | "cheque" | "demand_draft";

/**
 * Recording a payment the customer has made, against the order on screen.
 *
 * The order is taken from the address bar, not from `order_details_id`: the
 * form builds that with `parseInt` on the route, which turns "202610-000123"
 * into 202610 and would post the money to a different order's reference.
 * The acknowledgement number is the provider reference, which the server keeps
 * unique, so pressing submit twice cannot count the money twice.
 *
 * Captured at the counter, so it is recorded as received straight away. The
 * payment mode goes to core as the method, so its cash rule applies.
 */
export async function recordForexPayment(data: {
  method: CounterPaymentMethod;
  reference: string;
  amountMinor: string;
  proof: File;
  supporting?: File | null;
}) {
  const ref = orderRefFromRoute();
  try {
    const { data: payment } = await moneefloApi.post(
      `/orders/${encodeURIComponent(ref)}/payments`,
      {
        // The mode is what lets core apply the cash-limit rule. It is never "other".
        method: data.method,
        provider: "manual",
        providerRef: data.reference.trim(),
        amountMinor: data.amountMinor,
      },
    );

    // The proof (and any supporting document) is evidence for the payment just recorded.
    // If one cannot be stored the payment still stands, and the operator is told so
    // rather than being shown a plain failure and retrying.
    for (const [file, docType] of [
      [data.proof, "payment_proof"],
      [data.supporting ?? null, "payment_supporting"],
    ] as const) {
      if (!file) continue;
      try {
        const form = new FormData();
        // The file keeps its own name and type.
        form.append("file", file);
        form.append("docType", docType);
        // Ties the file to this payment, so it is never shown against another one.
        form.append("paymentId", payment.id);
        await moneefloApi.post(`/orders/${encodeURIComponent(ref)}/documents`, form);
      } catch {
        throw new Error(
          "The payment was recorded, but a document could not be attached. Do not submit it again.",
        );
      }
    }
    return payment;
  } catch (error) {
    if (error instanceof Error && error.message.startsWith("The payment was recorded")) {
      throw error;
    }
    throw coreError(error, "The payment could not be recorded.");
  }
}

/** Every document filed against the order, for the bundle download. */
export async function listForexOrderFiles() {
  const ref = orderRefFromRoute();
  const { data } = await moneefloApi
    .get<CoreDocuments>(`/orders/${encodeURIComponent(ref)}/documents`)
    .catch((error) => {
      throw coreError(error, "The documents could not be loaded.");
    });
  return data.documents.map((d) => ({
    id: d.id as unknown as number,
    doc_name: d.label || d.fileName || d.docType,
    document: {
      name: d.fileName ?? d.docType,
      url: d.url ?? "",
      type: d.contentType?.includes("pdf") ? "pdf" : "image",
      mime: d.contentType ?? "",
    },
  }));
}

/**
 * Cancelling the order on screen. A fee charged to the customer goes as
 * `customerFee` in rupees, which core records as its own charge line.
 */
export async function cancelForexOrder(data: {
  reason: string;
  /** The cancellation charge in paise, as a string: never a float. */
  feeMinor: string;
}) {
  const ref = orderRefFromRoute();
  const fee = /^\d+$/.test(data.feeMinor) ? BigInt(data.feeMinor) : 0n;
  try {
    const { data: result } = await moneefloApi.post(
      `/orders/${encodeURIComponent(ref)}/cancel`,
      {
        reason: data.reason?.trim() || undefined,
        // Core takes rupees; whole paise written with two decimals read back as the same paise.
        customerFee: fee > 0n ? `${fee / 100n}.${String(fee % 100n).padStart(2, "0")}` : undefined,
      },
    );
    return result;
  } catch (error) {
    throw coreError(error, "The order could not be cancelled.");
  }
}

/**
 * Giving money back on the order on screen. Core posts the mirror of a
 * payment already received, capped at what was received.
 */
export async function refundForexOrder(data: {
  /** In paise, as a string: never a float. */
  amountMinor: string;
  reason?: string;
  reference?: string;
}) {
  if (!/^\d+$/.test(data.amountMinor) || BigInt(data.amountMinor) <= 0n) {
    throw new Error("A refund amount is required.");
  }
  const ref = orderRefFromRoute();
  try {
    const { data: refund } = await moneefloApi.post(
      `/orders/${encodeURIComponent(ref)}/refunds`,
      {
        amountMinor: data.amountMinor,
        reason: data.reason?.trim() || "Refund",
        reference: data.reference?.trim() || undefined,
      },
    );
    return refund;
  } catch (error) {
    throw coreError(error, "The refund could not be recorded.");
  }
}

/** The lines were saved but the order was not confirmed; the blockers panel says why, so the message is short. */
export class OrderNotConfirmedError extends Error {
  reason: string;
  constructor(reason: string) {
    super("Lines saved. Order not confirmed. See the blockers.");
    this.name = "OrderNotConfirmedError";
    this.reason = reason;
  }
}

/**
 * Accept or reject each line, then move the order on when it can.
 *
 * Core takes one decision per line. A reject carries its reason. When no line
 * is left undecided and at least one was accepted, the order is confirmed
 * (the old "Order Accepted"); core's gate refuses that when the paperwork or
 * the rules are not met, and its own message is what the operator is shown.
 * Nothing is auto-cancelled: rejecting every line leaves the order as it is.
 */
export async function decideForexLines(data: {
  status: { id: string; status: "Accept" | "Reject"; reason_for_reject?: string }[];
}) {
  const ref = orderRefFromRoute();
  const base = `/orders/${encodeURIComponent(ref)}`;
  for (const line of data.status) {
    try {
      await moneefloApi.post(`${base}/lines/${encodeURIComponent(line.id)}/decision`, {
        status: line.status === "Accept" ? "accepted" : "rejected",
        reason: line.status === "Reject" ? line.reason_for_reject?.trim() || undefined : undefined,
      });
    } catch (error) {
      throw coreError(error, "A line could not be decided.");
    }
  }
  if (!data.status.some((l) => l.status === "Accept")) {
    return { confirmed: false };
  }
  try {
    await moneefloApi.post(`${base}/status`, { status: "confirmed" });
    return { confirmed: true };
  } catch (error) {
    // The lines are decided; the order is not confirmed. Say why, in core's
    // words, so the operator knows what to fix.
    const reason = coreError(error, "The order could not be confirmed.").message;
    throw new OrderNotConfirmedError(reason);
  }
}

/** Accepting or rejecting a payment that is waiting to be checked. */
export async function decideForexPayment(data: {
  payments_id: string | number;
  status: string;
  reason?: string;
}) {
  const ref = orderRefFromRoute();
  try {
    const { data: result } = await moneefloApi.post(
      `/orders/${encodeURIComponent(ref)}/payments/${encodeURIComponent(String(data.payments_id))}/status`,
      {
        status: data.status === "accept" ? "accept" : "reject",
        reason: data.reason?.trim() || undefined,
      },
    );
    return result;
  } catch (error) {
    throw coreError(error, "The payment could not be decided.");
  }
}

/** The order on screen's currency lines, for the cancel and refund pages. */
export async function getForexProductList() {
  const ref = orderRefFromRoute();
  let order: CoreOrder;
  try {
    order = (await moneefloApi.get<CoreOrder>(`/orders/${encodeURIComponent(ref)}`)).data;
  } catch (error) {
    throw coreError(error, "The order could not be loaded.");
  }
  return {
    id: 0,
    product: (order.items ?? [])
      .filter((i) => i.status !== "rejected")
      .map((i) => ({
        id: i.id,
        product_id: i.productId ?? 0,
        quantity: rupees(i.fxAmountMinor),
        inr_amount: rupees(i.inrAmountMinor),
        status: LINE_STATUS[i.status] ?? "",
        rate_and_margins: [
          { l1_commission: 0, l2_commission: 0, l3_commission: 0, l2_id: 0, ibr_rate: Number(i.rateMicros) / 1_000_000 },
        ],
        product_detail: { id: i.productId ?? 0, name: i.product, subCategory: "" },
        country_currency_want: i.currencyWant ?? "INR",
        country_currency_have: i.currencyHave ?? "INR",
      })),
  };
}

/** What the cancel page needs: the lines, what was paid and what core says can go back. */
export async function getForexCancelContext() {
  const ref = orderRefFromRoute();
  let order: CoreOrder;
  try {
    order = (await moneefloApi.get<CoreOrder>(`/orders/${encodeURIComponent(ref)}`)).data;
  } catch (error) {
    throw coreError(error, "The order could not be loaded.");
  }
  return {
    reference: order.orderNo.replace(/^(ORD-)?MF-/, ""),
    status: order.status,
    /** A draft or a quote has taken no money, so there is no fee and nothing to refund. */
    unconfirmed: order.status === "draft" || order.status === "quoted",
    totalPayableMinor: order.totalPayableMinor ?? "0",
    paidMinor: order.paidMinor ?? "0",
    refundableMinor: order.refundableMinor ?? "0",
    largestSingleRefundMinor: order.largestSingleRefundMinor ?? "0",
    lines: (order.items ?? [])
      .filter((i) => i.status !== "rejected")
      .map((i) => ({
        id: i.id,
        currency: i.currencyWant ?? "",
        fxAmountMinor: i.fxAmountMinor,
        rateMicros: i.rateMicros,
        inrAmountMinor: i.inrAmountMinor,
      })),
  };
}
